import { createFilter } from 'react-select';
import { ChipOption } from './ChipSelect';

/** A language option carrying the backend's English label alongside the localized one shown. */
export type LanguageOption = ChipOption<string> & { englishLabel: string };

const languageNames = (() => {
    try {
        return new Intl.DisplayNames(navigator.languages, { type: 'language', fallback: 'none' });
    } catch {
        return null;
    }
})();

// Casing and sorting follow the locale DisplayNames actually resolved, not the host default.
const displayLocale = languageNames?.resolvedOptions().locale;

const localizedLabel = (code: string, fallback: string) => {
    try {
        const name = languageNames?.of(code);
        return name ? name.charAt(0).toLocaleUpperCase(displayLocale) + name.slice(1) : fallback;
    } catch {
        return fallback;
    }
};

/**
 * Shows each language in the applicant's own locale where the browser can name it,
 * keeping the backend's English label otherwise. Re-sorted because the backend order
 * is by English label.
 */
export const localizeLanguageOptions = (options: ChipOption<string>[]): LanguageOption[] => {
    const localized = options.map((option) => localizedLabel(option.value, option.label));
    // CLDR aliases some codes onto one name (historically tl → Filipino, tw → Akan); options
    // that would read the same keep their English label or the picker lists identical entries.
    const duplicated = new Set(localized.filter((label, index) => localized.indexOf(label) !== index));
    return options
        .map((option, index) => ({
            value: option.value,
            label: duplicated.has(localized[index]) ? option.label : localized[index],
            englishLabel: option.label,
        }))
        .sort((a, b) => a.label.localeCompare(b.label, displayLocale));
};

/** Matches the typed text against the localized name, the English name and the code, so any finds the language. */
export const filterLanguageOption = createFilter({
    stringify: (option: { data: LanguageOption }) =>
        `${option.data.label} ${option.data.englishLabel} ${option.data.value}`,
});
